/**
 * This node's identity settings (GET /api/node-settings/public), set by a
 * superadmin in the Consents / Branding tabs (Account/Consents.jsx,
 * Account/NodeBranding.jsx). logoUrl replaces the default Vellby navbar logo —
 * see components/Legal, Routes/NavBar.jsx, Routes/PublicHeader.jsx.
 *
 * Two distinct legal entities can appear in the public legal copy — never
 * blurred into one joint "we" — because they hold distinct roles, not
 * alternate names for the same thing:
 *
 *   - PLATFORM_LEGAL_ENTITY_NAME — WellByCare Nordic AB, which builds and
 *     runs the Vellby platform every node runs on, as a data *processor*.
 *     Always named.
 *   - legalEntityName — the entity responsible for this one node, set by its
 *     superadmin. When set, it is the data *controller* for this node, and
 *     the contracting party in the Terms of Use. The backend keeps a
 *     required consent in sync describing this split — see
 *     services/node_settings.py's set_legal_entity_name.
 *
 * When legalEntityName is unset, there is no separate node owner: the
 * platform entity is both controller and processor for that node, so legal
 * copy should name it alone rather than inventing a second party.
 *
 * Cached at the module level like use_node_info.js: the first consumer
 * fetches, everyone else reads the shared value. Admin edits call
 * invalidateNodeSettings() so the page they're on picks up the new value.
 *
 *   const { legalEntityName, platformLegalEntityName, logoUrl } = useNodeSettings();
 */

import { useState, useEffect } from 'react';
import { useApi } from './use_api';

// The organization that builds and runs the Vellby platform and every node
// on it. This is a constant, not a fallback: it is always named in the
// legal pages — as data processor when a node has set its own controller
// entity below, or as the controller itself when it hasn't.
export const PLATFORM_LEGAL_ENTITY_NAME = 'WellByCare Nordic AB';

let cachedSettings = null;

export function invalidateNodeSettings() {
    cachedSettings = null;
}

export function useNodeSettings() {
    const api = useApi();
    const [settings, setSettings] = useState(cachedSettings);
    const [ready, setReady] = useState(cachedSettings !== null);

    useEffect(() => {
        if (cachedSettings) { setSettings(cachedSettings); setReady(true); return; }
        let alive = true;
        api.get('/api/node-settings/public', { cache: true })
            .then(data => { cachedSettings = data; if (alive) { setSettings(data); setReady(true); } })
            .catch(() => { if (alive) setReady(true); });
        return () => { alive = false; };
    }, [api]);

    const legalEntityName = settings?.legal_entity_name || null;

    return {
        ready,
        // This node's own entity — null when unset, so callers can tell the
        // difference between "this node has an operator" and "it doesn't".
        legalEntityName,
        platformLegalEntityName: PLATFORM_LEGAL_ENTITY_NAME,
        logoUrl: settings?.logo_url || null,
        // A superadmin's tweaks to the generated avatar that is the node's
        // logo until an image is uploaded — { seed, colors } or null.
        avatar: settings?.avatar || null,
        // The domain invite links point to (warmdatabase.com for warmdata) —
        // null while they use the node's *.vellby.com hostname. A superadmin
        // picks it on the DNS tab from the hosts this node is already served
        // on (use_node_domain.js); the backend refuses any other.
        customDomain: settings?.custom_domain || null,
    };
}

export default useNodeSettings;
